import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import useMessageLanguageByRoute from './useMessageLanguageByRoute';
import useFriendlyCaptchaSiteKey from './useFriendlyCaptchaSiteKey';
import { navigateToErrorPage } from './errorUtils';

const sdk = new FriendlyCaptchaSDK();

/**
 * Maps locale codes from our message bundle system (Java-style, e.g. `pt_BR`, `kr`) to
 * BCP 47 language tags expected by the Friendly Captcha SDK (e.g. `pt-BR`, `ko`).
 * Only entries where the two formats diverge are listed here.
 */
const LANGUAGE_MAP = {
    kr: 'ko',
    pt_BR: 'pt-BR',
    pt_PT: 'pt-PT',
    zh_TW: 'zh-TW',
};

// eslint-disable-next-line import/prefer-default-export
export const useFriendlyCaptcha = () => {
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [captchaError, setCaptchaError] = useState(false);
    const widgetContainerRef = useRef(null);
    const widgetRef = useRef(null);
    const tokenRef = useRef(null);
    const pendingResolveRef = useRef(null);
    const messageLanguage = useMessageLanguageByRoute();
    const history = useHistory();
    const [siteKey, siteKeyError] = useFriendlyCaptchaSiteKey();

    useEffect(() => {
        if (siteKeyError) {
            navigateToErrorPage(history, siteKeyError);
            return undefined;
        }

        if (!siteKey) {
            return undefined;
        }

        setCaptchaError(false);

        const widget = sdk.createWidget({
            element: widgetContainerRef.current,
            sitekey: siteKey,
            startMode: 'auto',
            language: LANGUAGE_MAP[messageLanguage] ?? messageLanguage,
        });

        widgetRef.current = widget;

        widget.addEventListener('frc:widget.complete', (e) => {
            const token = e.detail.response;
            setShowCaptcha(false);
            if (pendingResolveRef.current) {
                pendingResolveRef.current(token);
                pendingResolveRef.current = null;
                widgetRef.current?.reset();
            } else {
                tokenRef.current = token;
            }
        });

        widget.addEventListener('frc:widget.error', () => {
            setCaptchaError(true);
        });

        return () => {
            tokenRef.current = null;
            if (pendingResolveRef.current) {
                pendingResolveRef.current(null);
                pendingResolveRef.current = null;
            }

            if (widget) {
                widget.destroy();
            }
        };
    }, [messageLanguage, siteKey, siteKeyError, history]);

    const verifyCaptcha = () =>
        new Promise((resolve) => {
            if (tokenRef.current) {
                const token = tokenRef.current;
                tokenRef.current = null;
                widgetRef.current?.reset();
                resolve(token);
                return;
            }

            pendingResolveRef.current = resolve;
            setShowCaptcha(true);
        });

    const cancelCaptcha = () => {
        if (pendingResolveRef.current) {
            pendingResolveRef.current(null);
            pendingResolveRef.current = null;
            tokenRef.current = null;
            widgetRef.current?.reset();
        }
        setShowCaptcha(false);
        setCaptchaError(false);
    };

    /**
     * Styles to override Friendly Captcha widget styles.
     * Using !important since some internal styles are applied with the style attribute.
     */
    const widgetContainerCSS = css`
        width: 100% !important;
        overflow: visible !important;

        iframe {
            left: 0;
            top: 0;
        }
    `;

    const captchaWidget = <div css={widgetContainerCSS} ref={widgetContainerRef} />;

    return {
        verifyCaptcha,
        cancelCaptcha,
        showCaptcha,
        captchaWidget,
        captchaError,
    };
};
